import React, { useEffect, useState, useMemo } from "react";
import { db, auth } from "../firebase";
import { collection, query, where, getDocs, doc, getDoc, serverTimestamp, setDoc, orderBy, limit, runTransaction } from "firebase/firestore";
import { getOrInitStock, calculateClosing } from "../utils/stockUtils";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { useNavigate } from "react-router-dom";
import { getISTDate } from "../utils/dateUtils";

const CnfStockLedgerBags = () => {
    const navigate = useNavigate();
    const [userProfile, setUserProfile] = useState(null);
    const [skuMaster, setSkuMaster] = useState({});
    const [skuList, setSkuList] = useState([]);
    const [inventoryDate, setInventoryDate] = useState(getISTDate());
    const [inventoryRows, setInventoryRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) { navigate("/"); return; }
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                if (!["superadmin", "admin", "operator"].includes(profile.role)) {
                    alert("Unauthorized access");
                    navigate("/dashboard");
                    return;
                }
                setUserProfile(profile);
            }
        });
        return () => unsub();
    }, [navigate]);

    // Load SKUs
    useEffect(() => {
        if (!userProfile) return;
        const loadSkus = async () => {
            const q = query(collection(db, "skuMaster"), where("clientId", "==", userProfile.clientId));
            let snap = await getDocs(q);
            if (snap.empty) {
                const q2 = query(collection(db, "skuMaster"));
                snap = await getDocs(q2);
            }
            const map = {};
            const list = [];
            snap.forEach((d) => {
                const data = d.data();
                const identifier = String(data.sku || data.Name || "").trim().toUpperCase();
                map[identifier] = data;
                list.push(identifier);
            });
            setSkuMaster(map);
            setSkuList(list);
        };
        loadSkus();
    }, [userProfile]);

    // Load Stock Status
    const loadLedger = async (dateStr) => {
        if (!userProfile) return;
        setLoading(true);
        try {
            // Use CENTRALIZED UTILITY for robust carry-forward
            const summaryDoc = await getOrInitStock("cnf", userProfile.clientId, dateStr, userProfile.clientId);
            const inv = summaryDoc?.inventory || {};
            
            const salesSnapshot = await getDocs(query(
                collection(db, "distributor_invoices"),
                where("clientId", "==", userProfile.clientId),
                where("approvedDateOnly", "==", dateStr),
                where("source", "==", "CNF_TO_DISTRIBUTOR")
            ));

            const dailySales = {};
            salesSnapshot.forEach(doc => {
                const items = doc.data().items || {};
                Object.entries(items).forEach(([sku, qty]) => {
                    const skuUpper = sku.trim().toUpperCase();
                    dailySales[skuUpper] = (dailySales[skuUpper] || 0) + Number(qty);
                });
            });

            const rows = skuList.map(sku => {
                const skuUpper = sku.trim().toUpperCase();
                const m = skuMaster[skuUpper];
                const info = inv[skuUpper] || { opening: 0 };
                
                // Packets per Bag calculation
                const c_Bag = Number(m?.cartonsPerBag) || 1;
                const c_Ctn = Number(m?.packetsPerCarton) || 1;
                const pktsPerBag = c_Bag * c_Ctn;

                const opening = Number(info.opening || 0);
                const purchase = Number(info.purchase || 0) + Number(info.received || 0);
                const sale = dailySales[skuUpper] || Number(info.sale || 0) || Number(info.sold || 0);
                const closing = opening + purchase - sale;
                
                return {
                    sku: skuUpper,
                    name: m?.Name || skuUpper,
                    pktsPerBag: pktsPerBag,
                    opening: opening / pktsPerBag,
                    purchase: purchase / pktsPerBag,
                    sale: sale / pktsPerBag,
                    closing: closing / pktsPerBag
                };
            });

            setInventoryRows(rows);
        } catch (err) {
            console.error("Error loading C&F ledger:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userProfile && skuList.length > 0) {
            loadLedger(inventoryDate);
        }
    }, [inventoryDate, userProfile, skuList]);

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>C&F STOCK LEDGER (BAGS)</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Internal inventory tracking in Bag units. (Conversion based on SKU Config)</p>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                         <input 
                            type="date" 
                            className="form-input" 
                            value={inventoryDate} 
                            onChange={e => setInventoryDate(e.target.value)} 
                            style={{ padding: "10px 16px" }}
                        />
                        <button onClick={() => loadLedger(inventoryDate)} className="btn-secondary">REFRESH</button>
                    </div>
                </div>

                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                <th>SKU Description</th>
                                <th style={{ textAlign: "right" }}>Opening (Bags)</th>
                                <th style={{ textAlign: "right", color: "var(--success)" }}>Receipt (+)</th>
                                <th style={{ textAlign: "right", color: "var(--error)" }}>Sales (-)</th>
                                <th style={{ textAlign: "right", fontWeight: 900 }}>Closing Stock</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="5" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>Calculating balances...</td></tr>
                            ) : inventoryRows.length === 0 ? (
                                <tr><td colSpan="5" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>No inventory data found.</td></tr>
                            ) : (
                                inventoryRows.map((r, idx) => (
                                    <tr key={idx} style={{ background: r.closing < 0 ? "rgba(239, 68, 68, 0.05)" : "transparent" }}>
                                        <td>
                                            <div style={{ fontWeight: 800, fontSize: 13, color: "var(--primary)" }}>{r.sku}</div>
                                            <div style={{ fontSize: 11, fontWeight: 600, marginTop: 2 }}>{r.name}</div>
                                            <div style={{ fontSize: 9, color: "var(--text-muted)", marginTop: 2 }}>Config: 1 Bag = {r.pktsPerBag} Pkt</div>
                                        </td>
                                        <td style={{ textAlign: "right", fontWeight: 700 }}>{r.opening.toFixed(2)}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--success)" }}>{r.purchase.toFixed(2)}</td>
                                        <td style={{ textAlign: "right", fontWeight: 700, color: "var(--error)" }}>{r.sale.toFixed(2)}</td>
                                        <td style={{ textAlign: "right", fontWeight: 900, fontSize: 15, background: "var(--surface-hover)" }}>{r.closing.toFixed(2)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        <tfoot>
                            <tr style={{ background: "var(--text-main)", color: "white" }}>
                                <td style={{ fontWeight: 800 }}>TOTAL AGGREGATE (BAGS)</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.opening, 0).toFixed(2)}</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.purchase, 0).toFixed(2)}</td>
                                <td style={{ textAlign: "right", fontWeight: 800 }}>{inventoryRows.reduce((s, r) => s + r.sale, 0).toFixed(2)}</td>
                                <td style={{ textAlign: "right", fontWeight: 900, fontSize: 16 }}>{inventoryRows.reduce((s, r) => s + r.closing, 0).toFixed(2)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </Layout>
    );
};

export default CnfStockLedgerBags;
